import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { ApiService } from './api.service';
import { RoleService } from './role.service';
import { BehaviorSubject } from 'rxjs';
import { NotificationData } from './notification.service';
import { DialogMutexService } from './dialog-mutex.service';
import {
  getLicensePlateFromNotification,
  isManualLprCaptureNotification,
} from '../shared/utils/notification-display.util';
import {
  clearPersistedDismissedLprEntryKeys,
  getLprEntryIdFromNotification,
  getLprEntrySuppressionKeysFromNotification,
  persistDismissedLprEntryKeys,
  readPersistedDismissedLprKeys,
  removeStoredNotificationFromHistory,
} from '../shared/utils/lpr-notification-rehydrate.util';

export interface ModalState {
  isOpen: boolean;
  notification: NotificationData | null;
  shouldStore?: boolean; // Flag to indicate if notification should be stored in localStorage
}

/** How the modal should open relative to an already-visible or closing dialog. */
export type NotificationModalOpenBehavior = 'respect_state' | 'replace';

/** Order-request modals that should be replaced when a newer lane LPR event arrives. */
export const STALE_LPR_ORDER_REQUEST_TYPES = new Set<NotificationData['type']>([
  'create-order-request',
  'create-customer-request',
  'invalid-license-plate',
  'create-payment-request',
]);

/**
 * CWS-1575: popups waiting behind an open one. A burst of plates can queue many; beyond this the
 * oldest are dropped from the popup queue (they stay in the history / dropdown).
 */
export const MAX_QUEUED_NOTIFICATION_POPUPS = 25;

/** A queued popup older than this is for a car that has long moved on; skip it. */
export const QUEUED_NOTIFICATION_MAX_AGE_MS = 5 * 60 * 1000;

interface QueuedNotification {
  notification: NotificationData;
  shouldStore: boolean;
  queuedAt: number;
}

export interface OpenNotificationModalOptions {
  notification: NotificationData;
  /**
   * `respect_state` — queue behind an open or closing popup (shown one by one), honor dismiss suppression (Pusher).
   * `replace` — clear queue and show immediately (user action, checkout transition, manual LPR).
   */
  behavior?: NotificationModalOpenBehavior;
  /** Persist to notification_history when not shown immediately. */
  shouldStore?: boolean;
  /** Clear LPR dismiss-suppression before open (list/dropdown reopen). Defaults to true when behavior is `replace`. */
  clearDismissSuppression?: boolean;
}

/**
 * Service for managing the global modal notification state and persisting notification history.
 * @component
 * @selector app-modal-state-service
 * @example
 *   <app-modal-state-service [shouldStore]="true" (modalState$)="onModalState($event)"></app-modal-state-service>
 * @input boolean shouldStore - Optional flag used when showing a notification that indicates whether the notification should be persisted to history/localStorage (defaults to true when not provided on method calls).
 * @output Observable<ModalState> modalState$ - Observable stream that emits the current modal state (isOpen, current notification, shouldStore). Subscribe to react to modal open/close and notification changes.
 *
 * Purpose:
 *   - Centralized service to open/close an in-app modal for notifications, queue notifications that arrive while the modal is closing, and persist notification history to localStorage with deduplication and a max history size.
 *
 * Key properties:
 *   - modalState$ (Observable<ModalState>): public read-only observable for components to react to modal state changes.
 *   - notificationQueue (Array): internal queue used to hold notifications that arrive while the modal is closing.
 *   - isClosing (boolean): internal flag to protect against race conditions during modal close animations.
 *   - closingTimeout (any): timer handle used to delay processing queued notifications until the modal close animation finishes.
 *
 * Key methods:
 *   - storeNotificationInLocalStorage(notification: NotificationData): void
 *       Stores a notification in localStorage under 'notification_history', deduplicates by license plate (case-insensitive trimmed), keeps only the last 10 entries, and dispatches a 'notificationStored' CustomEvent with the updated count. Skips storage for empty or 'Unknown' license plates.
 *   - showModal(notification: NotificationData, shouldStore: boolean = true): void
 *       Safely opens the modal with the provided notification. If the modal is closing or already open, the notification is queued (and optionally stored) and shown after the current one closes; it never replaces the current modal.
 *   - hideModal(): void
 *       Initiates modal close flow, sets isClosing to prevent races, updates state to closed, and after a short timeout (slightly longer than the UI animation) processes the queue to show the next notification if present.
 *   - getCurrentState(): ModalState
 *       Returns the current ModalState snapshot from the BehaviorSubject.
 *   - clearNotificationQueue(): void
 *       Clears any queued notifications, cancels pending close timers and resets the isClosing flag (useful for cleanup or forced resets).
 *   - forceShowModal(notification: NotificationData, shouldStore: boolean = true): void
 *       Immediately replaces any current modal and active queue to force display of the given notification.
 *
 * Lifecycle / usage notes:
 *   - Provided in root (singleton) so it can be injected across the app.
 *   - Designed to coordinate with UI animation timing (uses a close timeout to avoid race conditions).
 *   - Emits modalState$ changes via a BehaviorSubject so subscribers get the latest state on subscribe.
 */
@Injectable({
  providedIn: 'root',
})
export class ModalStateService {
  private modalStateSubject = new BehaviorSubject<ModalState>({
    isOpen: false,
    notification: null,
    shouldStore: true,
  });

  public modalState$ = this.modalStateSubject.asObservable();

  // Queue for notifications that arrive while modal is closing
  private notificationQueue: QueuedNotification[] = [];
  private isClosing = false;
  /** Entry of the popup just closed, until its close animation ends; its echoes must not re-open it. */
  private closingEntryId = '';
  private closingTimeout: any = null;
  private readonly DISMISSED_ENTRY_TTL_MS = 5 * 60 * 1000; // 2 minutes
  private readonly SLOW_POPUP_MS = 2000;
  private dismissedNotificationKeys = new Map<string, number>(); // suppression key -> dismiss timestamp ms

  constructor(
    private logger: LoggerService,
    private api: ApiService,
    private roleService: RoleService,
    private dialogMutex: DialogMutexService
  ) {
    readPersistedDismissedLprKeys().forEach((dismissedAt, key) => {
      this.dismissedNotificationKeys.set(key, dismissedAt);
    });

    // Close UI when another dialog force-acquires the mutex and preempts us.
    this.dialogMutex.releases$.subscribe(owner => {
      if (owner !== 'notification-modal' || !this.modalStateSubject.value.isOpen) {
        return;
      }
      this.isClosing = false;
      if (this.closingTimeout) {
        clearTimeout(this.closingTimeout);
        this.closingTimeout = null;
      }
      this.notificationQueue = [];
      this.modalStateSubject.next({
        isOpen: false,
        notification: null,
        shouldStore: true,
      });
    });
  }

  private reportSlowPopupIfNeeded(notification: NotificationData | null, openedAt: number): void {
    const row = (notification?.data ?? {}) as Record<string, any>;
    const receivedAt = row['_lanePusherReceivedAtMs'];
    if (typeof receivedAt !== 'number') return;

    const createdAt = Date.parse(String(row['created_at'] ?? ''));
    const creationToPopupMs = Number.isFinite(createdAt) ? openedAt - createdAt : undefined;
    const receiveToPopupMs = openedAt - receivedAt;
    const elapsedMs = receiveToPopupMs;
    if (elapsedMs <= this.SLOW_POPUP_MS) return;

    this.api
      .post('order/tunnel-alert/slow-notification-popup', {
        elapsedMs,
        creationToPopupMs,
        receiveToPopupMs,
        notificationType: notification?.type,
        lprEntryId: row['lpr_entry_id'] ?? row['lprEntryId'],
        locationId: row['location_id'] ?? row['locationId'],
        plate: row['plate'] ?? row['vehicle_license_plate'],
      })
      .subscribe();
  }

  private showOpenModal(
    notification: NotificationData,
    shouldStore: boolean,
    reportSlowPopup = false
  ): void {
    if (!this.dialogMutex.tryAcquire('notification-modal')) {
      this.logger.debug(
        '📱 Dialog mutex busy; storing notification instead of opening NotificationModal.'
      );
      this.logger.trackNotification('modal_discarded_dialog_mutex', {
        notificationType: notification?.type,
        activeOwner: this.dialogMutex.getActiveOwner() ?? undefined,
      });
      if (shouldStore !== false) {
        this.storeNotificationInLocalStorage(notification);
      }
      return;
    }
    if (reportSlowPopup) {
      this.reportSlowPopupIfNeeded(notification, Date.now());
    }
    this.modalStateSubject.next({ isOpen: true, notification, shouldStore });
  }

  /** User explicitly opened this notification — allow reopen even after dismiss. */
  private clearDismissedSuppressionForNotification(notification: NotificationData | null): void {
    const keys = getLprEntrySuppressionKeysFromNotification(notification);
    keys.forEach(key => this.dismissedNotificationKeys.delete(key));
    clearPersistedDismissedLprEntryKeys(keys);
  }

  /**
   * Hold a notification that arrived while a popup is open or closing; it is shown after the
   * current one closes, in arrival order. It is also stored in the history right away, so
   * the bell has it even if the queue later drops or expires it.
   */
  private enqueueNotificationForLaterPopup(
    notification: NotificationData,
    shouldStore: boolean
  ): void {
    if (shouldStore !== false) {
      this.storeNotificationInLocalStorage(notification);
    }

    const incomingEntryId = getLprEntryIdFromNotification(notification);
    const onScreenEntryId =
      getLprEntryIdFromNotification(this.modalStateSubject.value.notification) ||
      this.closingEntryId;
    if (incomingEntryId && incomingEntryId === onScreenEntryId) {
      // A repeat (SSE/Pusher echo, status update) of the popup being shown or just closed.
      this.logger.debug('📱 Ignoring repeat of the entry already on screen.');
      return;
    }
    if (incomingEntryId) {
      this.notificationQueue = this.notificationQueue.filter(
        item => getLprEntryIdFromNotification(item.notification) !== incomingEntryId
      );
    }

    this.notificationQueue.push({ notification, shouldStore, queuedAt: Date.now() });
    if (this.notificationQueue.length > MAX_QUEUED_NOTIFICATION_POPUPS) {
      const dropped = this.notificationQueue.shift() as QueuedNotification;
      this.logger.trackNotification('modal_queue_overflow', {
        droppedEntryId: getLprEntryIdFromNotification(dropped.notification) || undefined,
        notificationType: dropped.notification.type,
      });
    }
    this.logger.debug(
      `📱 Queued notification behind the open popup (queue length: ${this.notificationQueue.length}).`
    );
  }

  /**
   * Show the next queued popup. The rest stay queued and show one by one as each is closed.
   * Entries past their max age are skipped (dismissal is checked when an event arrives). If another dialog owns the
   * mutex the queue is left intact for {@link flushDeferredNotifications}.
   */
  private drainNotificationQueueAfterClose(): void {
    while (this.notificationQueue.length > 0) {
      const nextNotification = this.notificationQueue[0];

      if (Date.now() - nextNotification.queuedAt > QUEUED_NOTIFICATION_MAX_AGE_MS) {
        this.notificationQueue.shift();
        continue;
      }

      if (!this.dialogMutex.tryAcquire('notification-modal')) {
        this.logger.debug('📱 Dialog mutex busy; keeping queued notifications for later.');
        // Our frame can sit beneath a stacked Way modal; give it up so it cannot block later popups.
        this.dialogMutex.release('notification-modal');
        return;
      }

      this.logger.debug('📱 Showing next queued notification.');
      this.notificationQueue.shift();
      // Not reported as a slow popup: the wait behind the previous popup is the attendant's, not the pipeline's.
      this.showOpenModal(nextNotification.notification, nextNotification.shouldStore, false);
      return;
    }

    this.dialogMutex.release('notification-modal');
  }

  /**
   * Store notification data with event in localStorage
   * Removes existing notifications with the same license plate (deduplication)
   * Keeps only the last 10 notifications
   * Dispatches custom event to notify other components
   */
  public storeNotificationInLocalStorage(notification: NotificationData): void {
    try {
      const storageKey = 'notification_history';
      const maxNotifications = 10;

      // Skip storage if license plate is "Unknown" or empty
      const licensePlate = getLicensePlateFromNotification(notification);
      if (!licensePlate || licensePlate === 'Unknown' || licensePlate.trim() === '') {
        this.logger.debug('⏭️ Skipping storage: License plate is Unknown or empty');
        return;
      }

      // Get existing notifications from localStorage
      const existingData = localStorage.getItem(storageKey);
      let notifications: { event: string; data: NotificationData; timestamp: string }[] = [];

      if (existingData) {
        try {
          notifications = JSON.parse(existingData);
          // Ensure it's an array
          if (!Array.isArray(notifications)) {
            notifications = [];
          }
        } catch (error) {
          this.logger.error('Error parsing existing notification data:', error);
          notifications = [];
        }
      }

      // Extract license plate from incoming notification
      const incomingLicensePlate = getLicensePlateFromNotification(notification);

      // Remove any existing notifications with the same license plate
      if (
        incomingLicensePlate &&
        incomingLicensePlate !== 'Unknown' &&
        incomingLicensePlate.trim() !== ''
      ) {
        notifications = notifications.filter(existingNotification => {
          const existingLicensePlate = getLicensePlateFromNotification(existingNotification.data);
          // Compare license plates (case-insensitive, trimmed)
          return (
            existingLicensePlate.trim().toUpperCase() !== incomingLicensePlate.trim().toUpperCase()
          );
        });
      }

      // Create notification entry with event type and data
      const notificationEntry = {
        event: notification.type ?? '',
        data: notification,
        timestamp: new Date().toISOString(),
      };

      // Add new notification to the beginning of the array
      notifications.unshift(notificationEntry);

      // Keep only the last 10 notifications (remove oldest if more than 10)
      if (notifications.length > maxNotifications) {
        notifications = notifications.slice(0, maxNotifications);
      }

      // Store back to localStorage
      localStorage.setItem(storageKey, JSON.stringify(notifications));

      // Dispatch custom event to notify other components (like header) about new notification
      window.dispatchEvent(
        new CustomEvent('notificationStored', {
          detail: { notificationCount: notifications.length },
        })
      );

      this.logger.debug(
        `✅ Notification stored in localStorage. Total notifications: ${notifications.length}`
      );
    } catch (error) {
      this.logger.error('❌ Error storing notification in localStorage:', error);
    }
  }

  /**
   * CWS-1730: whether this tablet shows, or has queued, the popup for an LPR entry. A
   * tablet with the popup must not also get that entry's toast.
   */
  public hasNotificationForLprEntry(lprEntryId: string | null | undefined): boolean {
    const entryId = String(lprEntryId ?? '').trim();
    if (!entryId) {
      return false;
    }
    const current = this.modalStateSubject.value;
    if (current.isOpen && getLprEntryIdFromNotification(current.notification) === entryId) {
      return true;
    }
    return this.notificationQueue.some(
      queued => getLprEntryIdFromNotification(queued.notification) === entryId
    );
  }

  /**
   * Single entry for opening the notification modal (Pusher, staff sale, list reopen, checkout transition).
   */
  public openNotificationModal(options: OpenNotificationModalOptions): void {
    // Attendance-only staff must not open notification / LPR / quick-sale modals.
    if (this.roleService.isStaff()) {
      return;
    }
    const {
      notification,
      behavior = 'respect_state',
      shouldStore = true,
      clearDismissSuppression = behavior === 'replace',
    } = options;

    if (behavior === 'replace') {
      this.openNotificationModalReplacing(notification, shouldStore, clearDismissSuppression);
      return;
    }

    this.openNotificationModalRespectingState(notification, shouldStore, clearDismissSuppression);
  }

  private openNotificationModalReplacing(
    notification: NotificationData,
    shouldStore: boolean,
    clearDismissSuppression: boolean
  ): void {
    this.logger.debug('🔨 Opening notification modal (replace)');
    if (clearDismissSuppression) {
      this.clearDismissedSuppressionForNotification(notification);
    }
    // Also cancels the close timer and resets `isClosing`.
    this.clearNotificationQueue();
    // Replace may preempt Way / feature owners; callers that own UI should hide peers first.
    this.dialogMutex.forceAcquire('notification-modal');
    this.modalStateSubject.next({ isOpen: true, notification, shouldStore });
  }

  private openNotificationModalRespectingState(
    notification: NotificationData,
    shouldStore: boolean,
    clearDismissSuppression: boolean
  ): void {
    const currentState = this.modalStateSubject.value;

    // Recover stuck isClosing only when there is no pending close-animation drain.
    // Interrupting an active closingTimeout drops a close-window popup (e.g. dismiss PUT echo
    // for the entry the user just closed arrives before the drain timer fires).
    if (!currentState.isOpen && this.isClosing && !this.closingTimeout) {
      this.logger.warn('⚠️ Modal state mismatch detected. Resetting closing flag.');
      this.isClosing = false;
    }

    if (!isManualLprCaptureNotification(notification)) {
      const shouldClearDismissSuppression =
        clearDismissSuppression || STALE_LPR_ORDER_REQUEST_TYPES.has(notification.type);
      if (shouldClearDismissSuppression) {
        this.clearDismissedSuppressionForNotification(notification);
      }

      const incomingSuppressionKeys = getLprEntrySuppressionKeysFromNotification(notification);
      for (const key of incomingSuppressionKeys) {
        const dismissedAt = this.dismissedNotificationKeys.get(key);
        if (dismissedAt && Date.now() - dismissedAt < this.DISMISSED_ENTRY_TTL_MS) {
          this.logger.debug(
            `📱 Suppressing duplicate notification for ${key} (dismissed ${Date.now() - dismissedAt}ms ago).`
          );
          if (shouldStore !== false) {
            this.storeNotificationInLocalStorage(notification);
          }
          return;
        }
      }
    }

    if (this.isClosing) {
      this.logger.debug('📱 Modal is closing. Queueing notification to show after close.');
      this.enqueueNotificationForLaterPopup(notification, shouldStore);
      return;
    }

    if (currentState.isOpen) {
      // Never swap an open popup; queue behind it so a burst of plates is shown one by one.
      this.logger.debug('📱 Modal is already open. Queueing notification behind it.');
      this.enqueueNotificationForLaterPopup(notification, shouldStore);
      return;
    }

    this.showOpenModal(notification, shouldStore, true);
  }

  /**
   * Attempt a standard notification while another overlay (e.g. Way purchase modal) is visible.
   * Does not open or dismiss the blocking overlay; popups queue behind an open one and are stored in history.
   */
  public deferNotificationUntilModalAvailable(
    notification: NotificationData,
    shouldStore = true
  ): void {
    if (this.isClosing) {
      this.enqueueNotificationForLaterPopup(notification, shouldStore);
      return;
    }
    if (this.modalStateSubject.value.isOpen) {
      this.logger.debug('📱 Modal is already open. Queueing notification behind it.');
      this.enqueueNotificationForLaterPopup(notification, shouldStore);
      return;
    }
    this.showOpenModal(notification, shouldStore, true);
  }

  /** Show the next deferred notification after a blocking overlay closes. */
  public flushDeferredNotifications(): void {
    if (this.isClosing || this.modalStateSubject.value.isOpen) {
      return;
    }
    if (this.notificationQueue.length === 0) {
      return;
    }
    this.drainNotificationQueueAfterClose();
  }

  /**
   * @deprecated Prefer {@link openNotificationModal} with `behavior: 'respect_state'`.
   **/
  public showModal(notification: NotificationData, shouldStore = true): void {
    this.openNotificationModal({ notification, behavior: 'respect_state', shouldStore });
  }

  /**
   * Close the modal, clear any closing timeout, mark the modal as closing, update modal state to closed, and after the closing animation show the next queued notification if available.
   * @example
   * hideModal()
   * undefined
   * @returns {void} No return value.
   */
  public hideModal(): void {
    // Mark as closing to prevent race conditions
    this.isClosing = true;

    // Clear any existing timeout
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }

    const currentNotification = this.modalStateSubject.value.notification;
    this.closingEntryId = getLprEntryIdFromNotification(currentNotification) || this.closingEntryId;
    const dismissedKeys = getLprEntrySuppressionKeysFromNotification(currentNotification);

    // CWS-863: a prompt the attendant closed stays closed across a refresh, whatever opened it.
    // This persisted key is the only thing `shouldRehydrateStoredLprNotification` reads, and it
    // used to sit inside the manual-capture exemption below — so closing an image-scan order
    // request recorded no dismissal and the next page load re-opened it, prefilled. A live
    // re-scan is unaffected: the duplicate check reads the in-memory map, and the order-request
    // types clear both sets on open (see STALE_LPR_ORDER_REQUEST_TYPES).
    if (dismissedKeys.length > 0) {
      persistDismissedLprEntryKeys(dismissedKeys, Date.now());
    }

    // Remember dismissed LPR row id for duplicate Pusher events (not manual image uploads),
    // so re-uploading the same capture within this session still re-opens the modal.
    if (!isManualLprCaptureNotification(currentNotification)) {
      if (dismissedKeys.length > 0) {
        const now = Date.now();
        dismissedKeys.forEach(key => this.dismissedNotificationKeys.set(key, now));
      }
      if (currentNotification) {
        removeStoredNotificationFromHistory(currentNotification);
      }
    }

    // Update state to close modal
    this.modalStateSubject.next({
      isOpen: false,
      notification: null,
      shouldStore: true,
    });

    // Wait for modal closing animation to complete (300ms based on animation duration)
    // Then check queue and show next notification if available.
    // Keep the mutex until after the drain so deferred Way/feature opens cannot race the queue.
    this.closingTimeout = setTimeout(() => {
      this.isClosing = false;
      this.closingEntryId = '';

      if (this.notificationQueue.length > 0) {
        this.drainNotificationQueueAfterClose();
      } else {
        this.dialogMutex.release('notification-modal');
      }

      this.closingTimeout = null;
    }, 350); // Slightly longer than animation duration (300ms) to ensure clean state
  }

  public getCurrentState(): ModalState {
    return this.modalStateSubject.value;
  }

  /**
   * Clear the notification queue (useful for cleanup or reset scenarios)
   */
  public clearNotificationQueue(): void {
    this.logger.debug('🧹 Clearing notification queue');
    this.notificationQueue = [];
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }
    this.isClosing = false;
    this.closingEntryId = '';
  }

  /**
   * @deprecated Prefer {@link openNotificationModal} with `behavior: 'replace'`.
   */
  public forceShowModal(notification: NotificationData, shouldStore = true): void {
    this.openNotificationModal({
      notification,
      behavior: 'replace',
      shouldStore,
      clearDismissSuppression: true,
    });
  }
}
